import React, { createElement, useEffect } from "react";
import "./App.css";
import LogoPNG from "./logo-circular.png";
import MartyWavingSVG from "./assets/marty-waving.svg"; // <-- Add import
//import { ESPLoader, connect } from 'esp-web-flasher';
import { ESPLoader, connect } from "adafruit-webserial-esptool";
import { CurrentVersionDef } from "./CurrentVersionDef";
import RICRunningFirmware from "./RICRunningFirmware";
import { RICSysInfoMsg } from "./RICTypes";
import RICUpdater from "./RICUpdater";
import { Line } from "rc-progress";
import RICRevision from "./RICRevision";
import VersionSelector from "./components/VersionSelector";
import {
  RICConnector,
  RICFileDownloadResult,
  RICHWElemList_Str,
  RICProgressCBType,
  RICHWFWUpdRslt,
  RICSystemInfo,
} from "@robotical/ricjs";
import { RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR } from "@robotical/ricjs-robotical-addons/dist/RICRoboticalAddOns";
import modalState from "./state-observables/modal/ModalState";
import ColourSensorCalibrationInstructionsModal from "./components/modals/ColourSensorCalibrationInstructionsModal";
import ColourSensorCalibrationProcess from "./components/modals/ColourSensorCalibrationProcess";
import ErrorDetailModal, { ErrorAction } from "./components/modals/ErrorDetailModal";
import { setErrorLogVersions } from "./errorLogging";

// Update path
const updateBasePath =
  "https://updates.robotical.io/[[FW_SOURCE]]/martyv2/rev[[REV_NUMBER]]/[[FW_VERSION]]";

const COLOUR_SENSOR_FW_NAME = "fw00000097-1.3.0.rfw";
const PUBLIC_URL_BASE = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
const COLOUR_SENSOR_FW_PATH = `${PUBLIC_URL_BASE}/firmware/${COLOUR_SENSOR_FW_NAME}`;
const COLOUR_SENSOR_FORCE_CMD = `hwfwupd/RSAddOn/${COLOUR_SENSOR_FW_NAME}/LeftColorSensor/force`;
const DEV_LAST_ACTION_KEY = "ric-web-flasher/dev-last-action";
const SUCCESS_QUERY_PARAM = "success";
const SUCCESS_QUERY_VALUE = "1";
const SUCCESS_MESSAGE = "Firmware update successful";

// Handle updating of RIC
const GET_SYSTEM_INFO_FROM_RIC_SERIAL = false;

const ric: RICConnector = new RICConnector();

function logMsg(...args: any[]) {
  console.log(...args);
}

function debugMsg(...args: any[]) {
  console.debug(...args);
}

function errorMsg(...args: any[]) {
  console.error(...args);
}

  function formatMacAddr(macAddr: number[]) {
    return macAddr
      .map((value) => value.toString(16).toUpperCase().padStart(2, "0"))
      .join(":");
  }

async function getCurrentVersionJson(
  fwSource: string
): Promise<CurrentVersionDef> {
  const response = await fetch(
    updateBasePath.replace("[[FW_SOURCE]]", fwSource),
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );
  const json = await response.json();
  return json;
}

async function connectToESP(): Promise<ESPLoader | null> {
  let espLoader: ESPLoader | null = null;
  try {
    espLoader = await connect({
      log: (...args) => logMsg(...args),
      debug: (...args) => debugMsg(...args),
      error: (...args) => errorMsg(...args),
    });
  } catch (e) {
    console.error(`connectToESP: failed ${e}`);
    return null;
  }
  console.log(
    `connectToESP: espLoader = ${espLoader} isConn = ${espLoader.connected}`
  );

  // Check connected ok
  if (!espLoader || !espLoader.connected) {
    return null;
  }
  return espLoader;
}

async function cleanupESPLoader(
  espLoader: ESPLoader | null,
  { resumeRicConnection = false }: { resumeRicConnection?: boolean } = {}
) {
  if (!espLoader) return;
  try {
    await espLoader.disconnect();
  } catch (err) {
    console.error(`Error during espLoader disconnect: ${err}`);
  }
  try {
    if (espLoader.port && typeof espLoader.port.close === "function") {
      await espLoader.port.close();
    }
  } catch (err) {
    console.error(`Error closing espLoader port: ${err}`);
  }
  if (resumeRicConnection) {
    try {
      ric.pauseConnection(false);
    } catch (err) {
      console.error("Failed to resume RIC connection after cleanup:", err);
    }
  }
}

async function initialiseESPLoader(
  espLoader: ESPLoader,
  timeoutCallback: () => void
): Promise<boolean> {
  // Initialise esp loader
  console.log(`connectToESP: initialising espLoader`);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      espLoader.initialize(),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          timeoutCallback();
          reject(new Error("Timed out initialising ESP loader"));
        }, 10000);
      }),
    ]);
  } catch (e) {
    await cleanupESPLoader(espLoader);
    console.error(`Failed to initialise ESPLoader: ${e}`);
    return false;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  console.log(`connectToESP: espLoader initialised`);

  logMsg("Connected to " + espLoader.chipName);
  logMsg("MAC Address: " + formatMacAddr(espLoader.macAddr()));

  return true;
}

async function startGettingSystemInfo(
  espLoader: ESPLoader,
  sysInfoCB: (sysInfo: RICSysInfoMsg) => void
) {
  // Hard reset
  await espLoader.hardReset();

  // Send v command
  await new Promise((resolve) => setTimeout(resolve, 2000));
  await espLoader.writeToStream(["v".charCodeAt(0), "\n".charCodeAt(0)]);

  // See if RIC Firmware is running and get version if so
  const ricRunningFirmware = new RICRunningFirmware(
    espLoader,
    espLoader.logger,
    sysInfoCB
  );

  // Start getting system info
  ricRunningFirmware.start();
}

function getStatusStr(
  loader: ESPLoader | null,
  flashSize: string | null,
  sysInfoJSON: string | null,
  ricVersion: string | null,
  ricHwRevision: number | null,
  progressMessage: string,
  progressPC: number,
  progressState: string
) {
  const progressClass =
    "status-item" +
    (progressState === "error"
      ? " status-warn"
      : progressPC === 100
        ? " status-complete"
        : "");
  return (
    <div className="status-line">
      {/* {loader ? <div className='status-item'>Chip Name: {loader.chipName}</div> : <div></div>} */}
      {/* {flashSize ? <div className='status-item'>Flash Size: {flashSize}</div> : <div></div>} */}
      {ricVersion ? (
        <div className="status-item">RIC Version: {ricVersion}</div>
      ) : (
        <div></div>
      )}
      {ricHwRevision ? (
        <div className="status-item">
          RIC HW Revision: {ricHwRevision} (Flash Size {flashSize})
        </div>
      ) : (
        <div></div>
      )}
      {sysInfoJSON ? (
        <div className="status-item">System Info: {sysInfoJSON}</div>
      ) : (
        <div></div>
      )}
      {progressMessage ? (
        <div className={progressClass}>
          <div className="status-label">Progress: {progressMessage}</div>
          <div>
            <Line percent={progressPC} strokeWidth={3} strokeColor="#ffda61" />
          </div>
        </div>
      ) : (
        <div></div>
      )}
    </div>
  );
}

function LandingPage() {
  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const RECONNECT_BAUD = 115200;
  const MAX_RECONNECT_ATTEMPTS = 5;

  const [loader, setLoader] = React.useState<ESPLoader | null>(null);
  const [sysInfoJSON, setSysInfoJSON] = React.useState<string | null>(null);
  const [ricVersion, setRICVersion] = React.useState<string | null>(null);
  const [ricHwRevision, setRICHwRevision] = React.useState<number | null>(null);
  const [retryCount, setRetryCount] = React.useState<number>(0);
  const [progressMessage, setProgressMessage] = React.useState<string>("");
  const [progressPC, setProgressPC] = React.useState<number>(0);
  const [progressState, setProgressState] = React.useState<string>("ok");
  const [flashSize, setFlashSize] = React.useState<string | null>(null);
  const [fwSource, setFwSource] = React.useState<string>("live");
  const [isDev, setIsDev] = React.useState<boolean>(false);
  const [devFwVersion, setDevFwVersion] = React.useState<string>(
    "current_version.json"
  );
  const [error, setError] = React.useState<string | null>(null);
  const [versions, setVersions] = React.useState<string[]>([]);
  const [eraseFirst, setErasefirst] = React.useState<boolean>(false);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [elemUpdatesRequired, setElemUpdatesRequired] =
    React.useState<boolean>(false);
  const [failureMessage, setFailureMessage] = React.useState<string | null>(
    null
  );
  const [finishUpdateRequired, setFinishUpdateRequired] =
    React.useState<boolean>(false);
  const [devLastAction, setDevLastAction] = React.useState<string>(() => {
    if (typeof window === "undefined") return "none";
    try {
      return localStorage.getItem(DEV_LAST_ACTION_KEY) || "none";
    } catch (err) {
      console.warn("Could not read last dev action from storage:", err);
      return "none";
    }
  });

  const describeError = (err: unknown) => {
    if (err instanceof Error) return err.message;
    if (typeof err === "string") return err;
    try {
      return JSON.stringify(err);
    } catch {
      return "Unknown error";
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(DEV_LAST_ACTION_KEY, devLastAction);
    } catch (err) {
      console.warn("Could not store last dev action:", err);
    }
  }, [devLastAction]);

  useEffect(() => {
    setErrorLogVersions({ targetVersion: devFwVersion });
  }, [devFwVersion]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(SUCCESS_QUERY_PARAM) === SUCCESS_QUERY_VALUE) {
      setProgressState("ok");
      setProgressPC(100);
      setProgressMessage(`${SUCCESS_MESSAGE} 100%`);
    }
  }, []);

  const recordDevAction = (action: string) => {
    setDevLastAction(action);
  };

  const showErrorModal = ({
    title = "Something went wrong",
    message,
    details,
    troubleshootingSteps = [],
    actions,
    supportLink,
    tone,
  }: {
    title?: string;
    message: string;
    details?: string;
    troubleshootingSteps?: string[];
    actions?: ErrorAction[];
    supportLink?: string;
    tone?: "info" | "warning";
  }) => {
    modalState.setModal(
      createElement(ErrorDetailModal, {
        message,
        details,
        troubleshootingSteps,
        actions,
        supportLink,
        tone,
      }),
      title
    );
  };

  async function openSerialPortClean(locator: SerialPort, baudRate: number) {
    try {
      await locator.open({ baudRate });
      return;
    } catch (err: any) {
      if (err && err.name === "InvalidStateError") {
        console.warn("Port already open, closing and reopening for a clean session");
        try {
          await locator.close();
        } catch (closeErr) {
          console.error(`Error closing port after InvalidStateError: ${closeErr}`);
        }
        await locator.open({ baudRate });
        return;
      }
      if (
        err &&
        (err.name === "NetworkError" ||
          (typeof err.message === "string" &&
            err.message.toLowerCase().includes("disconnected")))
      ) {
        throw new Error("Serial port disconnected; please reconnect Marty");
      }
      throw err;
    }
  }

  async function reconnectAfterFlash(expectedVersion: string) {
    let lastError: unknown = null;
    const locator = ric.getConnLocator() as SerialPort | null;
    const channel = ric.getRICChannel();
    if (!locator || !channel || typeof locator.open !== "function") {
      return { ok: false, reason: "reconnect" as const };
    }

    for (let attempt = 0; attempt < MAX_RECONNECT_ATTEMPTS; attempt++) {
      try {
        await delay(attempt ? 500 * attempt : 0);
        await openSerialPortClean(locator, RECONNECT_BAUD);
        ric.pauseConnection(false);

        console.log(`RIC Channel connected: ${channel.isConnected()}`);
        if (!channel.isConnected()) {
          await channel.connect("reusePort");
        }

        const sysInfo = await ric
          .getRICMsgHandler()
          .sendRICRESTURL<RICSystemInfo>("v");
        if (!sysInfo || !sysInfo.SystemVersion) {
          throw new Error("System info missing after reconnect");
        }
        console.log(`Detected new system version ${sysInfo.SystemVersion}`);
        setRICVersion(sysInfo.SystemVersion);
        setErrorLogVersions({ currentVersion: sysInfo.SystemVersion });
        if (
          expectedVersion !== "" &&
          sysInfo.SystemVersion !== expectedVersion &&
          attempt === 0
        ) {
          throw new Error(
            `Detected version (${sysInfo.SystemVersion}) does not match expected (${expectedVersion})`
          );
        }
        lastError = null;
        break;
      } catch (err) {
        lastError = err;
        console.error(`Reconnect attempt ${attempt + 1} failed: ${err}`);
      }
    }

    if (lastError) {
      console.warn(
        "Automatic reconnect failed, prompting user for manual reconnect"
      );
      return { ok: false, reason: "reconnect" as const };
    }

    return { ok: true };
  }

  const firmwareBaseUrl = "https://updates.robotical.io";

  useEffect(() => {
    if (!isWebSerialSupported()) {
      setError("Your browser is not compatible with the Marty Updater");
      alert("Your browser is not compatible with the Marty Updater");
    }
    const asyncFunc = async () => {
      const fwVersions = await getDevVersions();
      if (!fwVersions) return;
      setVersions(fwVersions || []);
      //setDevFwVersion(fwVersions![0] || "current_version.json");
    };
    asyncFunc();
  }, []);

  function onProgress(state: string, progressPC: number, isOk: boolean) {
    logMsg(`${state}: ${progressPC}%`);
    if (progressPC !== 0) {
      setProgressMessage(state + " " + progressPC.toFixed(0) + "%");
    } else {
      setProgressMessage(state);
    }
    setProgressPC(progressPC);
    setProgressState(isOk ? "ok" : "error");
  }

  async function getDevVersions() {
    const fetchResponse = await fetch(
      `${firmwareBaseUrl}/testing/martyv2/versions.json`
    );
    if (!fetchResponse || !fetchResponse.ok) return [];
    const versionsJson = await fetchResponse.json();
    let versions = versionsJson["versions"];
    versions.unshift("current_version.json");
    return versions;
  }

  function isWebSerialSupported() {
    return "serial" in navigator;
  }

  function espLoaderTimeout() {
    console.error("espLoaderTimeout");
    setLoader(null);
    setProgressMessage(
      "Cannot connect: please refresh the page and try again ensuring the port is correct and nothing else is using the connection"
    );
    setProgressPC(0);
    setProgressState("error");
  }

  useEffect(() => {
    checkForDevFw();
  });

  function checkForDevFw() {
    const params = window.location.search;
    console.log("params: " + params);
    if (params.includes("dev")) setIsDev(true);
  }

  function refreshWithSuccessParam() {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set(SUCCESS_QUERY_PARAM, SUCCESS_QUERY_VALUE);
    window.location.replace(url.toString());
  }

  async function checkIsColourSensCalibrationRequired(): Promise<{
    required: boolean;
    error?: string;
  }> {
    console.log("\n==== Checking if colour sensor calibration is required ====");
    try {
      // check if the connected RIC has at least one coloursensor on version 1.3.0, with DTID of 0x0097
      // if so, we need to calibrate the colour sensor after updating
      const ricHWListStr = await ric
        .getRICMsgHandler()
        .sendRICRESTURL<RICHWElemList_Str>(`hwstatus/strstat/?filterByType=${RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR}`);
      if (!ricHWListStr || !ricHWListStr.hw) {
        throw new Error("No hardware status returned");
      }

      const hwElems = ricHWListStr.hw;
      if (hwElems && hwElems.length) {
        if (typeof hwElems[0] !== "object") {
          // we are on a fw version that doesn't support strstat, so we can assume that they don't have a batch 5 coloursensor
          return { required: false };
        } else {
          const hwElemList = RICHWElemList_Str.expand(ricHWListStr);
          console.log("hwElemList: ", hwElemList);
          const colourSensElems = hwElemList.hw.filter(
            (elem) => elem.whoAmI === RIC_WHOAMI_TYPE_CODE_ADDON_COLOUR
          );
          console.log("colourSensElems: ", colourSensElems);
          // check if any of the colour sensors are on version 1.3.0
          const isAnyColourSensOn130 =
            colourSensElems &&
            !!colourSensElems.length &&
            colourSensElems.some((elem) => elem.versionStr === "1.3.0");
          console.log("isAnyColourSensOn130: ", isAnyColourSensOn130);
          return { required: isAnyColourSensOn130 };
        }
      }
      return { required: false };
    } catch (err) {
      console.error("Failed to check colour sensor calibration requirement:", err);
      return { required: false, error: describeError(err) };
    }
  }

  function selectVersionHandler(version: string) {
    setDevFwVersion(version);
  }

  async function downloadFile(
    downloadUrl: string,
    progressCB: RICProgressCBType = (received, total) => { }
  ): Promise<RICFileDownloadResult> {
    const fileDownloadResult = new RICFileDownloadResult();
    console.log(`Fetching ${downloadUrl}`);
    const fetchResponse = await fetch(downloadUrl);
    if (!fetchResponse.ok) {
      console.log(`Failed to get firmware file ${downloadUrl}`);
      return fileDownloadResult;
    }
    const fileBlob = await fetchResponse.blob();
    fileDownloadResult.fileData = new Uint8Array(await fileBlob.arrayBuffer());
    fileDownloadResult.downloadedOk = true;
    return fileDownloadResult;
  }

  function getUpdatePath(
    revNo: number | null = ricHwRevision,
    fwVersionParam: string = devFwVersion,
    fwSourceParam: string = fwSource
  ) {
    console.log(
      `fwSource: ${fwSourceParam} devFwVersion ${fwVersionParam} revNo ${revNo ?? ricHwRevision}`
    );
    let updatePath = updateBasePath.replace("[[FW_SOURCE]]", fwSourceParam);

    if (fwVersionParam === "current_version.json" || fwVersionParam === "") {
      updatePath = updatePath.replace("[[FW_VERSION]]", "current_version.json");
    } else {
      updatePath = updatePath.replace(
        "[[FW_VERSION]]",
        fwVersionParam + "/" + fwVersionParam + ".json"
      );
    }

    if (revNo) {
      updatePath = updatePath.replace("[[REV_NUMBER]]", revNo.toString());
    }

    return updatePath;
  }

  async function getVersionFromPath(path: string) {
    try {
      const fetchResponse = await fetch(path);
      if (!fetchResponse || !fetchResponse.ok) return "";
      const versionFileJson = await fetchResponse.json();
      return versionFileJson["firmwareVersion"];
    } catch (err) {
      console.log(`Error getting expected version from file ${path} - ${err}`);
      return "";
    }
  }

  async function updateESP(
    useRICport = true,
    expectedVersion = "",
    fwVersionParam: string = devFwVersion,
    fwSourceParam: string = fwSource
  ): Promise<{ ok: boolean; reason?: "reconnect" | "flash" }> {
    let espLoader: ESPLoader | null = null;
    if (useRICport) {
      ric.pauseConnection(true);
      // use existing port from ricjs connection
      espLoader = new ESPLoader(ric.getConnLocator(), {
        log: (...args) => logMsg(...args),
        debug: (...args) => debugMsg(...args),
        error: (...args) => errorMsg(...args),
      });
    } else {
      espLoader = await connectToESP();
    }

    const cleanupOptions = { resumeRicConnection: useRICport };
    const safeCleanup = async () => {
      await cleanupESPLoader(espLoader, cleanupOptions);
    };

    if (!espLoader) return { ok: false, reason: "flash" };

    setProgressMessage("Connecting, please wait...");
    if (!(await initialiseESPLoader(espLoader, espLoaderTimeout))) {
      await safeCleanup();
      return { ok: false, reason: "flash" };
    }

    // Set the loader
    console.log("Setting loader");
    setLoader(espLoader);
    console.log("Setting loader done");

    // Hardware revision
    const ricRevision = new RICRevision();

    console.log(`Getting system info`);

    // Start getting System Info
    // TODO: consolidate this functionality
    if (GET_SYSTEM_INFO_FROM_RIC_SERIAL) {
      await startGettingSystemInfo(espLoader, (sysInfo: RICSysInfoMsg) => {
        console.log("System Info: ", sysInfo);
        setSysInfoJSON(JSON.stringify(sysInfo));
        if (sysInfo.v) {
          setRICVersion(sysInfo.v);
          setErrorLogVersions({ currentVersion: sysInfo.v });
        }
        if (sysInfo.r) {
          setRICHwRevision(sysInfo.r);
        }
      });

      const currVersion = await getCurrentVersionJson(fwSource);
      console.log(currVersion);
    }

    // Run the ESP loader stub
    let espStub = null;
    try {
      espStub = await espLoader.runStub();
    } catch (err) {
      console.error("Failed to run ESP stub:", err);
      await safeCleanup();
      return { ok: false, reason: "flash" };
    }
    if (!espStub) {
      await safeCleanup();
      return { ok: false, reason: "flash" };
    }

    // Get flash size
    const flashSize = espStub.flashSize;

    // Compute RIC revision
    if (flashSize) {
      ricRevision.set(flashSize ?? "");
      setRICHwRevision(ricRevision.hwRevision);
      setFlashSize(flashSize);
    }

    const updatePath = getUpdatePath(
      ricRevision.hwRevision,
      fwVersionParam,
      fwSourceParam
    );

    console.log("updatePath: " + updatePath);
    // Start updating RIC
    const ricUpdater = new RICUpdater(
      espLoader,
      espStub,
      eraseFirst,
      onProgress
    );

    let mainFlashCompleted = false;
    try {
      await ricUpdater.update(updatePath);
      mainFlashCompleted = true;
    } catch (err) {
      console.error(`Main flash failed: ${err}`);
      await safeCleanup();
      return { ok: false, reason: "flash" };
    }

    if (useRICport) {
      // wait for RIC to finish starting up
      await delay(10000);
      // Disconnect
      await safeCleanup();

      const reconnectResult = await reconnectAfterFlash(expectedVersion);
      if (!reconnectResult.ok) {
        console.error(`Error communicating with ric after update`);
        return {
          ok: false,
          reason: mainFlashCompleted ? "reconnect" : "flash",
        };
      }
    } else {
      await safeCleanup();
    }

    return { ok: true };
  }

  async function elemsToUpdateWithRetry(
    maxAttempts = 3,
    retryDelayMs = 1000
  ): Promise<{ elems: Array<any> | null; lastError?: unknown; attempts: number }> {
    let lastError: unknown = null;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const elems = await ric.getRICUpdateManager()!.elemUpdatesRequired();
        if (elems !== null) return { elems, attempts: attempt + 1 };
      } catch (err) {
        lastError = err;
        console.error(`elemUpdatesRequired attempt ${attempt + 1} failed: ${err}`);
      }
      await delay(retryDelayMs);
    }
    if (lastError) {
      console.error(
        `Failed to determine element updates after ${maxAttempts} attempts: ${lastError}`
      );
    }
    return { elems: null, lastError, attempts: maxAttempts };
  }

  const elemOnlyUpdateHandler = async () => {
    setProgressState("ok");
    setProgressPC(5);
    setProgressMessage("Reconnecting to run element updates...");
    setRetryCount(0);
    setIsUpdating(true);

    try {
      const connected = await ric.connect("WebSerial", "");
      if (!connected) {
        setProgressState("error");
        setProgressMessage("Could not reconnect. Please check the cable and retry.");
        showErrorModal({
          title: "Can't reconnect to Marty",
          message: "We couldn't open the USB connection to finish servo and sensor updates.",
          troubleshootingSteps: [
            "Check the USB cable and port selection then try again",
            "Close any other apps that may be using the same serial port",
            "Unplug Marty, plug back in, and click Retry",
          ],
          actions: [
            {
              label: "Retry connection",
              onClick: elemOnlyUpdateHandler,
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
        return;
      }

      ric.setupUpdateManager(
        "",
        "",
        `${firmwareBaseUrl}/${fwSource}`,
        downloadFile
      );

      try {
        const sysInfo = await ric.getRICSystem().getRICSystemInfo(true);
        const systemVersion =
          (sysInfo as any)?.SystemVersion ?? (sysInfo as any)?.["SystemVersion"];
        if (systemVersion) {
          setRICVersion(systemVersion);
          setErrorLogVersions({ currentVersion: systemVersion });
        }
        if (sysInfo && sysInfo.RicHwRevNo) {
          setRICHwRevision(sysInfo.RicHwRevNo);
        }
      } catch (err) {
        console.error(`Failed to get system info before elem-only update: ${err}`);
      }

      let isColourSensCalibrationRequired = false;
      let colourCheckError: string | undefined;
      try {
        const colourCheck = await checkIsColourSensCalibrationRequired();
        isColourSensCalibrationRequired = colourCheck.required;
        colourCheckError = colourCheck.error;
        if (isColourSensCalibrationRequired) {
          await modalState.setModal(
            createElement(ColourSensorCalibrationInstructionsModal),
            "Colour Sensor Calibration Required"
          );
        }
      } catch (err) {
        console.error("Colour sensor calibration check failed before elem-only update:", err);
      }

      try {
        await ric.getRICMsgHandler().sendRICRESTURL("pwrctrl/5von");
        await delay(1000);
      } catch (err) {
        console.error(`Failed to enable power before elem-only update: ${err}`);
      }

      const { elems: elemsToUpdate, lastError } = await elemsToUpdateWithRetry();
      if (!elemsToUpdate) {
        const msg =
          "Lost connection while checking which servos or sensors need updates.";
        setProgressState("error");
        setProgressMessage(msg);
        setFailureMessage(msg);
        showErrorModal({
          title: "Servo/sensor update stalled",
          message: msg,
          details: lastError ? describeError(lastError) : undefined,
          troubleshootingSteps: [
            "Reconnect Marty and make sure the USB cable is stable",
            "Rerun the servo and sensor update to finish the process",
          ],
          actions: [
            {
              label: "Retry servo/sensor update",
              onClick: elemOnlyUpdateHandler,
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
        return;
      }

      if (!elemsToUpdate.length) {
        onProgress("Element updates successful", 100, true);
      } else {
        onProgress("Updating servos and addons...", 80, true);
        const elemsUpdatedOk = await ric.getRICUpdateManager()!.updateElems(elemsToUpdate);
        if (!elemsUpdatedOk) {
          setProgressState("error");
          setProgressMessage(
            "Element update failed. Please reconnect Marty and retry."
          );
          showErrorModal({
            title: "Servo/sensor update failed",
            message: "Element update failed. Please reconnect Marty and retry.",
            troubleshootingSteps: [
              "Reconnect Marty and keep the USB cable steady during the update",
              "Run the servo/sensor update again to finish any partial updates",
            ],
            actions: [
              {
                label: "Retry servo/sensor update",
                onClick: elemOnlyUpdateHandler,
                closeBeforeAction: true,
              },
              { label: "Close", variant: "secondary" },
            ],
          });
        } else {
          onProgress("Element updates successful", 100, true);
        }
      }
      setFinishUpdateRequired(false);

      if (isColourSensCalibrationRequired) {
        console.log("Colour sensor calibration required (elem-only path)");
        await modalState.setModal(
          createElement(ColourSensorCalibrationProcess, { ricConnector: ric }),
          "Colour Sensor Calibration Required"
        );
      } else if (colourCheckError) {
        showErrorModal({
          title: "Could not check colour sensor",
          message:
            "We couldn't confirm if the colour sensor needs calibration because the connection was interrupted.",
          details: colourCheckError,
          troubleshootingSteps: [
            "Reconnect Marty and ensure the USB cable is secure",
            "Retry the servo/sensor update if you want to double-check accessories",
          ],
          actions: [
            {
              label: "Retry servo/sensor update",
              onClick: elemOnlyUpdateHandler,
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
      }
      await delay(500);
    } catch (err) {
      console.error("Element-only update failed:", err);
      const msg =
        "Something went wrong while updating servos/add-ons. Please reconnect and retry.";
      setProgressState("error");
      setProgressMessage(msg);
      setFailureMessage(msg);
      showErrorModal({
        title: "Servo/sensor update failed",
        message: msg,
        details: describeError(err),
        troubleshootingSteps: [
          "Reconnect Marty and ensure no other app is using the USB port",
          "Click Retry to run the servo/sensor update again",
        ],
        actions: [
          { label: "Retry servo/sensor update", onClick: elemOnlyUpdateHandler },
          { label: "Close", variant: "secondary" },
        ],
      });
    } finally {
      try {
        await ric.disconnect();
      } catch (disconnectErr) {
        console.error("Error disconnecting after servo/sensor update:", disconnectErr);
      }
      setIsUpdating(false);
    }
  };

  const startUpdate = async (
    overrideConfig?: { fwSource?: string; fwVersion?: string; lastAction?: string }
  ) => {
    if (isUpdating) return;

    const selectedFwSource = overrideConfig?.fwSource ?? fwSource;
    const selectedFwVersion = overrideConfig?.fwVersion ?? devFwVersion;
    if (overrideConfig?.fwSource) setFwSource(overrideConfig.fwSource);
    if (overrideConfig?.fwVersion) setDevFwVersion(overrideConfig.fwVersion);
    if (overrideConfig?.lastAction) recordDevAction(overrideConfig.lastAction);
    setErrorLogVersions({
      currentVersion: null,
      targetVersion: selectedFwVersion,
    });

    // Clear state
    setProgressPC(0);
    setProgressMessage("");
    setProgressState("ok");
    setLoader(null);
    setRICVersion(null);
    setRetryCount(0);
    setRICHwRevision(null);
    setIsUpdating(true);
    setElemUpdatesRequired(false);
    setFailureMessage(null);
    setFinishUpdateRequired(false);

    let refreshAfterSuccess = false;

    try {
      const connected = await ric.connect("WebSerial", "");
      if (!connected) {
        setProgressState("error");
        setProgressMessage(
          "Couldn't open the USB connection. Please check the cable and port selection."
        );
        showErrorModal({
          title: "Can't start update",
          message: "We couldn't open a connection to Marty.",
          troubleshootingSteps: [
            "When prompted, choose the CP2102N/USB Serial port and click Connect",
            "Close any other app that might be using the same serial port",
            "Unplug Marty, plug back in, and try again",
          ],
          actions: [
            {
              label: "Retry connection",
              onClick: () => startUpdate(overrideConfig),
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
        return;
      }
      console.log(`RIC conn locator ` + ric.getConnLocator());
      ric.setupUpdateManager(
        "",
        "",
        `${firmwareBaseUrl}/${selectedFwSource}`,
        downloadFile
      );

      let sysInfo: RICSystemInfo | null = null;
      try {
        sysInfo = await ric.getRICSystem().getRICSystemInfo(true);
      } catch (err) {
        console.error(`Failed to get system info before update: ${err}`);
      }

      let elemsToUpdate: Array<any> | null = null;
      let expectedVersion = "";
      let skipMainESPUpdate = false;
      let colourCheckError: string | undefined;

      // if there was a failed previous update, we won't be able to get system version or query hwelems
      // the main ESP update will still function and will rescue the board
      let isColourSensCalibrationRequired = false;
      if (sysInfo && sysInfo["SystemVersion"] != "0.0.0") {
        console.log("sysinfo: " + JSON.stringify(sysInfo));
        const systemVersion = sysInfo["SystemVersion"];
        if (systemVersion) {
          setRICVersion(systemVersion);
          setErrorLogVersions({ currentVersion: systemVersion });
        }

        const colourSensCheckResult = await checkIsColourSensCalibrationRequired();
        isColourSensCalibrationRequired = colourSensCheckResult.required;
        colourCheckError = colourSensCheckResult.error;
        if (isColourSensCalibrationRequired) {
          await modalState.setModal(
            createElement(ColourSensorCalibrationInstructionsModal),
            "Colour Sensor Calibration Required"
          );
        }

        const revNo = sysInfo.RicHwRevNo;
        console.log(`Detected RIC fw v${systemVersion} revision ${revNo}`);
        if (revNo) setRICHwRevision(revNo);

        expectedVersion = await getVersionFromPath(
          getUpdatePath(revNo, selectedFwVersion, selectedFwSource)
        );
        console.log(`Updating to version ${expectedVersion}`);
        setErrorLogVersions({
          targetVersion: expectedVersion || selectedFwVersion,
        });
        skipMainESPUpdate =
          expectedVersion !== "" &&
          systemVersion !== "" &&
          systemVersion === expectedVersion;
        if (skipMainESPUpdate) {
          onProgress(
            "Main firmware already up to date, continuing with element updates",
            5,
            true
          );
        }

        // ensure 5v power is on, in case robot has been idle for a while
        await ric.getRICMsgHandler().sendRICRESTURL("pwrctrl/5von");
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const elemCheck = await elemsToUpdateWithRetry();
        elemsToUpdate = elemCheck.elems;
        setElemUpdatesRequired(!!(elemCheck.elems && elemCheck.elems.length));
      }

      // TODO:
      // check if any updates are required
      // alert user if no updates required

      // Handle failure
      const espResult = skipMainESPUpdate
        ? { ok: true }
        : await updateESP(true, expectedVersion, selectedFwVersion, selectedFwSource);
      if (!espResult.ok) {
        const reconnectFailure = espResult.reason === "reconnect";
        setRetryCount(reconnectFailure ? 0 : 1);
        setProgressState(reconnectFailure ? "ok" : "error");
        const msg = reconnectFailure
          ? "Main robot update complete, servo and sensor update required. Click the button below to continue."
          : "Main firmware update failed before servo/sensor updates could run.";
        setProgressMessage(msg);
        setFailureMessage(reconnectFailure ? null : msg);
        showErrorModal({
          title: reconnectFailure ? "Main robot update completed" : "Update failed",
          message: msg,
          tone: reconnectFailure ? "info" : "warning",
          troubleshootingSteps: [
            reconnectFailure
              ? "Click Finish Update to run servo and sensor updates"
              : "Reconnect Marty and ensure the USB cable is stable",
            reconnectFailure
              ? "Keep the cable connected during the servo/sensor step"
              : "Start the update again to retry the main firmware",
          ],
          actions: [
            reconnectFailure
              ? {
                label: "Finish update",
                onClick: elemOnlyUpdateHandler,
                closeBeforeAction: true,
              }
              : {
                label: "Retry update",
                onClick: () => startUpdate(overrideConfig),
                closeBeforeAction: true,
              },
            { label: reconnectFailure ? "Close" : "Cancel", variant: "secondary" },
          ],
        });
        if (reconnectFailure) {
          setFinishUpdateRequired(true);
          setProgressPC((prev) => (prev && prev < 80 ? 80 : prev || 80));
        } else {
          setLoader(null);
        }
        return;
      }

      onProgress("Updating servos and addons...", 80, true);

      try {
        await ric.getRICMsgHandler().sendRICRESTURL("hwstatus");
      } catch (err) {
        console.error("Failed to fetch hwstatus after main update:", err);
      }

      // check again elemsToUpdate and isColourSensCalibrationRequired, in case original fw was corrupted and we didn't get this before
      const elemCheckAfter = await elemsToUpdateWithRetry();
      elemsToUpdate = elemCheckAfter.elems;
      const colourSensCheckResultAfter = await checkIsColourSensCalibrationRequired();
      isColourSensCalibrationRequired =
        isColourSensCalibrationRequired || colourSensCheckResultAfter.required;
      colourCheckError = colourSensCheckResultAfter.error;

      if (!elemsToUpdate) {
        const msg =
          "We lost connection while checking which servos or sensors need updates.";
        setProgressState("error");
        setProgressMessage(msg);
        setFailureMessage(msg);
        showErrorModal({
          title: "Servo/sensor check failed",
          message: msg,
          details: elemCheckAfter.lastError
            ? describeError(elemCheckAfter.lastError)
            : colourCheckError,
          troubleshootingSteps: [
            "Reconnect Marty and keep the USB cable connected",
            "Click 'Update Servos and Sensors' to finish the update",
          ],
          actions: [
            {
              label: "Update Servos and Sensors",
              onClick: elemOnlyUpdateHandler,
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
        return;
      }

      if (elemsToUpdate.length) {
        if (!(await ric.getRICUpdateManager()!.updateElems(elemsToUpdate))) {
          setLoader(null);
          setRetryCount(1);
          setProgressState("error");
          const msg = "Element update failed. Please reconnect Marty and retry.";
          setProgressMessage(msg);
          setFailureMessage(msg);
          showErrorModal({
            title: "Servo/sensor update failed",
            message: msg,
            troubleshootingSteps: [
              "Reconnect Marty and keep the USB cable stable",
              "Click 'Update Servos and Sensors' to retry the servo/add-on update",
            ],
          actions: [
            {
              label: "Update Servos and Sensors",
              onClick: elemOnlyUpdateHandler,
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
      }
      } else {
        onProgress("Element updates successful", 100, true);
      }

      // starting colour sensor calibration process if required
      if (isColourSensCalibrationRequired) {
        console.log("Colour sensor calibration required");
        await modalState.setModal(
          createElement(ColourSensorCalibrationProcess, { ricConnector: ric }),
          "Colour Sensor Calibration Required"
        );
      }

      if (colourCheckError) {
        showErrorModal({
          title: "Could not check colour sensor",
          message:
            "We couldn't confirm if the colour sensor needs calibration because the connection was interrupted.",
          details: colourCheckError,
          troubleshootingSteps: [
            "Reconnect Marty and ensure the USB cable is secure",
            "Run 'Update Servos and Sensors' if you want to double-check accessories",
          ],
          actions: [
            {
              label: "Update Servos and Sensors",
              onClick: elemOnlyUpdateHandler,
              closeBeforeAction: true,
            },
            { label: "Close", variant: "secondary" },
          ],
        });
      }

      onProgress(SUCCESS_MESSAGE, 100, true);
      refreshAfterSuccess = true;
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (err) {
      console.error("Update failed:", err);
      setProgressState("error");
      const msg =
        "Something went wrong during the update. Reconnect Marty and try again.";
      setProgressMessage(msg);
      setFailureMessage(msg);
      showErrorModal({
        title: "Update failed",
        message: msg,
        details: describeError(err),
        troubleshootingSteps: [
          "Reconnect Marty and restart the update",
          "If the main flash succeeded, run 'Update Servos and Sensors' to finish",
        ],
        actions: [
          {
            label: "Restart update",
            onClick: () => startUpdate(overrideConfig),
            closeBeforeAction: true,
          },
          {
            label: "Update Servos and Sensors",
            onClick: elemOnlyUpdateHandler,
            variant: "secondary",
            closeBeforeAction: true,
          },
          { label: "Close", variant: "ghost" },
        ],
      });
    } finally {
      try {
        await ric.disconnect();
      } catch (err) {
        console.error("Error during disconnect:", err);
      }
      setIsUpdating(false);
      if (refreshAfterSuccess) {
        refreshWithSuccessParam();
      }
    }
  };

  const startUpdateHandler = async () => {
    await startUpdate();
  };

  async function fetchColourSensorFirmware(): Promise<Uint8Array | null> {
    try {
      const resp = await fetch(COLOUR_SENSOR_FW_PATH);
      if (!resp.ok) {
        setProgressState("error");
        setProgressMessage(
          `Could not load colour sensor firmware (${resp.status}).`
        );
        return null;
      }
      const buffer = await resp.arrayBuffer();
      return new Uint8Array(buffer);
    } catch (err) {
      console.error(`Failed to fetch colour sensor firmware: ${err}`);
      setProgressState("error");
      setProgressMessage("Failed to load colour sensor firmware.");
      return null;
    }
  }

  async function pollColourSensorUpdater(): Promise<RICHWFWUpdRslt | null> {
    let finalStatus: RICHWFWUpdRslt | null = null;
    for (let attempt = 0; attempt < 20; attempt++) {
      try {
        const status = await ric
          .getRICMsgHandler()
          .sendRICRESTURL<RICHWFWUpdRslt>("hwfwupd");
        finalStatus = status;
        if (status?.st?.m) {
          setProgressMessage(status.st.m);
        }
        if (typeof status?.st?.p === "number") {
          setProgressPC((prev) =>
            Math.max(prev, Math.min(100, status.st.p))
          );
        }
        if (status?.st?.s === "done" || status?.st?.s === "idle") {
          break;
        }
      } catch (err) {
        console.error(`Failed to poll colour sensor updater: ${err}`);
        break;
      }
      await delay(2000);
    }
    return finalStatus;
  }

  const startColourSensorUpdate = async () => {
    if (isUpdating) return;
    recordDevAction("Update left colour sensor to v1.3.0");

    setProgressPC(1);
    setProgressMessage("Preparing colour sensor update...");
    setProgressState("ok");
    setLoader(null);
    setRICVersion(null);
    setRetryCount(0);
    setRICHwRevision(null);
    setElemUpdatesRequired(false);
    setFailureMessage(null);
    setIsUpdating(true);

    const firmwareData = await fetchColourSensorFirmware();
    if (!firmwareData) {
      setIsUpdating(false);
      return;
    }

    const connected = await ric.connect("WebSerial", "");
    if (!connected) {
      setIsUpdating(false);
      setProgressState("error");
      setProgressMessage("Could not connect to Marty. Check the cable and retry.");
      return;
    }

    try {
      try {
        await ric.getRICMsgHandler().sendRICRESTURL("pwrctrl/5von");
      } catch (err) {
        console.warn(`Could not enable 5V before colour sensor update: ${err}`);
      }

      setProgressMessage("Uploading colour sensor firmware...");
      const uploadOk = await ric.sendFile(
        COLOUR_SENSOR_FW_NAME,
        firmwareData,
        (_, __, progress) => {
          const pct = Math.min(50, Math.floor(progress * 100));
          setProgressPC(pct);
          setProgressMessage(
            `Uploading colour sensor firmware... ${Math.floor(progress * 100)}%`
          );
        }
      );
      if (!uploadOk) {
        setProgressState("error");
        setProgressMessage("Uploading colour sensor firmware failed.");
        return;
      }

      setProgressPC(60);
      setProgressMessage("Starting left colour sensor update...");

      const startResp = await ric
        .getRICMsgHandler()
        .sendRICRESTURL<RICHWFWUpdRslt>(COLOUR_SENSOR_FORCE_CMD);
      console.log("Colour sensor update start response", startResp);

      const finalStatus = await pollColourSensorUpdater();
      const statusState = finalStatus?.st?.s;
      if (statusState === "done" || statusState === "idle") {
        setProgressPC(100);
        setProgressMessage("Left colour sensor updated to v1.3.0.");
        setProgressState("ok");
      } else {
        setProgressState("error");
        setProgressMessage("Colour sensor update did not complete. Please retry.");
      }
    } catch (err) {
      console.error(`Colour sensor update failed: ${err}`);
      setProgressState("error");
      setProgressMessage("Colour sensor update failed. Please retry.");
    } finally {
      try {
        await ric.disconnect();
      } catch (err) {
        console.error(`Error disconnecting after colour sensor update: ${err}`);
      }
      setIsUpdating(false);
    }
  };

  const startCurrentVersionUpdate = () =>
    startUpdate({
      fwSource: "live",
      fwVersion: "current_version.json",
      lastAction: "Update to current_version.json",
    });

  const startV135Update = () =>
    startUpdate({
      fwSource: "testing",
      fwVersion: "v1.3.5",
      lastAction: "Update to v1.3.5",
    });

  return (
    <div className="App">
      <div className="header-block">
        <div className="header-group">
          <img
            src={LogoPNG}
            alt="Robotical Logo"
            className="robotical-logo"
          />
          <div
            id="pageHeadingId"
            className="content-head-item content-master-head"
          >
            Marty Update
          </div>
        </div>
      </div>
      <div className="marty-waving-container">
        <img
          src={MartyWavingSVG}
          alt="Marty Waving"
          className="marty-waving-img"
        />
      </div>
      {/* Show Status while updating, on retry, or after completion (progress > 0) */}
      {(isUpdating || retryCount > 0 || progressPC > 0) && (
      <div className="content-block">
        <h3 className="update-status">Status</h3>
        <div className="content-options">
          <div className="content-status">
            {error && <div className="status-item status-warn">{error}</div>}
          </div>
          <div className="content-status">
            {retryCount > 0 ? (
              <div className="status-item status-warn">
                {failureMessage ||
                  "Please refresh the page, check cables, check nothing else using serial port, and retry"}
              </div>
            ) : (
              <div className="status-box">
                {getStatusStr(
                  loader,
                  flashSize,
                  sysInfoJSON,
                  ricVersion,
                  ricHwRevision,
                  progressMessage,
                  progressPC,
                  progressState
                )}
                {finishUpdateRequired && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <div className="status-item">
                      Main robot update complete, servo and sensor update required. Click below to finish.
                    </div>
                    <button
                      className="button-large"
                      style={{ marginTop: "0.5rem" }}
                      onClick={elemOnlyUpdateHandler}
                      disabled={isUpdating}
                    >
                      Finish Update
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      )}
      {!isUpdating && (
        <div className="content-block instructions-block">
          <div className="instructions-content">
            <ol className="content-list">
              <li className="content-item">
                Connect Marty to your computer using a USB cable
              </li>
              <li className="content-item">
                Click the button below to update Marty
              </li>
            </ol>
            <div className="content-info">
              <p>
                After you click you will see a pop-up saying this program <i>"wants to
                connect to a serial port."</i>
              </p>
              <p>
                If one of the options says <b>"CP2102N"</b> or <b>"USB Serial"</b> then please
                select that one.
              </p>
              <p>After making a selection please press Connect.</p>
            </div>
          </div>
          <div className="instructions-action-row">
            <button className="button-large" onClick={startUpdateHandler}>
              Start Update
            </button>
          </div>
        </div>
      )}
      <div
        className="content-block"
        id="devFirmare"
        style={{ display: isDev ? "flex" : "none", flexDirection: "column", alignItems: "center" }}
      >
        <div style={{ marginBottom: "1rem", width: "100%", textAlign: "center" }}>
          <span style={{ fontWeight: 600, color: "#446084", fontSize: "1.1rem" }}>Firmware source:</span>
          <select
            value={fwSource}
            onChange={(event) => setFwSource(event.target.value)}
          >
            <option value="live">Live</option>
            <option value="testing">Testing</option>
          </select>
        </div>
        <div
          style={{
            display: "flex",
            gap: "0.5rem",
            flexWrap: "wrap",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <button className="button-large" onClick={startCurrentVersionUpdate} disabled={isUpdating}>
            Update to current_version.json
          </button>
          <button className="button-large" onClick={startV135Update} disabled={isUpdating}>
            Update to v1.3.5
          </button>
          <button className="button-large" onClick={startColourSensorUpdate} disabled={isUpdating}>
            Update left colour sensor to v1.3.0
          </button>
        </div>
        <div
          style={{
            marginTop: "0.5rem",
            fontSize: "0.9rem",
            color: "#446084",
          }}
        >
          last action: {devLastAction}
        </div>
      </div>

      {fwSource === "testing" && (
        <div className="content-block">
          <VersionSelector
            versions={versions}
            onChange={selectVersionHandler}
          />
        </div>
      )}

      {fwSource === "testing" && (
        <div className="content-block" style={{ flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
          <label htmlFor="eraseFirst">Erase first?</label>
          <input
            type="checkbox"
            id="eraseFirst"
            name="eraseFirst"
            value="eraseFirst"
            checked={eraseFirst}
            onChange={(event) => setErasefirst(event.target.checked)}
          />
        </div>
      )}

      
    </div>
  );
}

export default LandingPage;
